import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import { Box, useTheme } from "@mui/material";

import { EditorCanvas } from "./EditorCanvas";
import type { AspectRatio, DragOffset } from "./helpers";
import { HoverOverlay } from "./HoverOverlay";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import type { FitImageResult } from "@/components/ImageUploader/utils";

export interface SquareImageEditorStatelessProps {
  /** The original image data URL */
  imageDataUrl: string;
  /** The processed/fitted image data */
  fittedImage: FitImageResult | null;
  /** Detected aspect ratio of the image */
  aspectRatio: AspectRatio | null;
  /** Current drag offset for positioning */
  dragOffset: DragOffset;
  /** Whether fit mode is enabled (show full image with letterboxing) */
  isFitMode: boolean;
  /** Whether the image is still loading/processing */
  isLoading: boolean;
  /** Target output size in pixels */
  targetSize: number;
  /** Called when drag interaction starts */
  onDragStart?: (e: React.MouseEvent | React.TouchEvent) => void;
  /** Called when fit mode is toggled */
  onFit?: () => void;
  /** Called when replace button is clicked */
  onReplace?: () => void;
  /** Called when delete button is clicked */
  onDelete?: () => void;
  /** Called when confirm is triggered (via ref) */
  onConfirm: () => Promise<void>;
  /** Called when viewport size changes due to resize */
  onViewportSizeChange?: (size: number) => void;
  /** Force hover state to be shown (for testing in Storybook) */
  forceHover?: boolean;
  /** Show fit button in center instead of replace button (hides top-left fit button) */
  showCenterFitButton?: boolean;
  /** Use white background instead of blurred image background */
  useWhiteBackground?: boolean;
}

export type SquareImageEditorStatelessHandle = {
  confirm: () => Promise<void>;
};

export const SquareImageEditorStateless = forwardRef<SquareImageEditorStatelessHandle, SquareImageEditorStatelessProps>(
  (
    {
      imageDataUrl,
      fittedImage,
      aspectRatio,
      dragOffset,
      isFitMode,
      isLoading,
      targetSize,
      onDragStart,
      onFit,
      onReplace,
      onDelete,
      onConfirm,
      onViewportSizeChange,
      forceHover = false,
      showCenterFitButton = false,
      useWhiteBackground = false,
    },
    ref
  ) => {
    const { t } = useTranslate();
    const theme = useTheme();
    const { isSmallScreen } = useMediaQuery();

    const [viewportSize, setViewportSize] = useState(targetSize);
    const [isHovering, setIsHovering] = useState(false);

    const showHoverOverlay = forceHover || isHovering || isSmallScreen;
    const viewportRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const updateViewportSize = useCallback(() => {
      if (!viewportRef.current) {
        return;
      }

      const rect = viewportRef.current.getBoundingClientRect();
      const size = Math.min(rect.width, rect.height);
      setViewportSize((prevSize) => {
        if (Math.abs(prevSize - size) > 0.5) {
          onViewportSizeChange?.(size);
          return size;
        }
        return prevSize;
      });
    }, [onViewportSizeChange]);

    useImperativeHandle(
      ref,
      () => ({
        confirm: () => onConfirm(),
      }),
      [onConfirm]
    );

    useEffect(() => {
      if (!viewportRef.current) {
        return;
      }

      updateViewportSize();

      const resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updateViewportSize);
      });

      resizeObserver.observe(viewportRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }, [updateViewportSize]);

    if (isLoading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
          <p>{t("common", "loading")}</p>
        </Box>
      );
    }

    const isSquare = aspectRatio === "square";
    const canDrag = !isSquare && !isFitMode;
    const cursor = isSquare || isFitMode ? "default" : aspectRatio === "portrait" ? "ns-resize" : "ew-resize";

    return (
      <Box
        ref={viewportRef}
        sx={{
          width: "100%",
          height: "100%",
          maxWidth: `${targetSize}px`,
          maxHeight: `${targetSize}px`,
          position: "relative",
          overflow: "hidden",
          backgroundColor: theme.palette.surface.neutral.supershy,
          cursor,
          borderRadius: "8px",
        }}
        onMouseDown={canDrag ? onDragStart : undefined}
        onTouchStart={canDrag ? onDragStart : undefined}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {fittedImage && (
          <EditorCanvas
            ref={imageRef}
            imageDataUrl={imageDataUrl}
            fittedImage={fittedImage}
            aspectRatio={aspectRatio}
            dragOffset={dragOffset}
            isFitMode={isFitMode}
            targetSize={targetSize}
            viewportSize={viewportSize}
            onImageLoad={updateViewportSize}
            useWhiteBackground={useWhiteBackground}
          />
        )}

        <HoverOverlay
          onReplace={onReplace}
          onDelete={onDelete}
          onFit={isSquare ? undefined : onFit}
          showFitButton={!isSquare && !showCenterFitButton}
          showDragText={!isSquare && !isFitMode}
          showCenterFitButton={showCenterFitButton}
          isFitMode={isFitMode}
          aspectRatio={aspectRatio}
          isVisible={showHoverOverlay}
          targetSize={targetSize}
          viewportSize={viewportSize}
        />
      </Box>
    );
  }
);

SquareImageEditorStateless.displayName = "SquareImageEditorStateless";
